import React, { useRef, useState } from 'react';
import { AspectRatio } from '@chakra-ui/react';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Image } from '../../../components/UI-CHAKRA/Image';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { BannerData } from '../../../shared/bannerApi';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import {
  EMISSION_LOADING_BANNER_CLICK_EVENT,
  EMISSION_LOADING_BANNER_VIEW_EVENT,
  trackEmissionLoadingBannerEvent,
} from '../../../stores/helpers/emissionLoadingBannerEvent';
import { EMISSION_LOADING_COLORS, FONT_ONEST } from './constants';

interface SponsoredSlotProps {
  banner: BannerData;
  isMobile: boolean;
}

/**
 * Slot patrocinado 1:1 do loading de assinatura e envio da receita.
 *
 * O slot só conta como exibido quando o criativo carrega: a visualização é registrada no `onLoad`
 * da imagem, e não na montagem. Se o criativo falhar (rede, arquivo indisponível ou URL assinada
 * expirada), o slot some para o card voltar a mostrar só os passos, sem área vazia.
 */
export function SponsoredSlot({ banner, isMobile }: SponsoredSlotProps) {
  const hasTrackedView = useRef(false);
  const [hasImageError, setHasImageError] = useState(false);

  const getEventContext = () => {
    const { prescription, prescriberConfiguration } = usePrescriptionStore.getState();

    return {
      prescription,
      prescriber: prescription?.Prescritor || null,
      prescriberConfiguration,
      idBanner: banner.idBanner,
      isMobile,
    };
  };

  const handleImageLoad = () => {
    if (hasTrackedView.current) return;

    hasTrackedView.current = true;
    trackEmissionLoadingBannerEvent(EMISSION_LOADING_BANNER_VIEW_EVENT, getEventContext());
  };

  const handleClick = () => {
    if (!banner.link) return;

    window.open(banner.link, '_blank', 'noopener,noreferrer');
    trackEmissionLoadingBannerEvent(EMISSION_LOADING_BANNER_CLICK_EVENT, getEventContext());
  };

  const bannerUrl = isMobile ? banner.mobile || banner.desktop : banner.desktop || banner.mobile;

  if (hasImageError) return null;

  return (
    <Box
      position={'relative'}
      flexShrink={0}
      w={{ base: '100%', md: '372px' }}
      borderRadius={{ base: '18px', md: '20px' }}
      overflow={'hidden'}
      bg={EMISSION_LOADING_COLORS.sponsoredSlotBg}
      cursor={banner.link ? 'pointer' : 'default'}
      onClick={handleClick}
      data-testid={'emission-sponsored-slot'}
    >
      {/* AspectRatio usa padding em vez de aspect-ratio, que não é suportado no Chrome 86 (ambiente MV). */}
      <AspectRatio ratio={1}>
        <Image
          src={bannerUrl}
          alt={banner.alt}
          objectFit={'cover'}
          onLoad={handleImageLoad}
          onError={() => setHasImageError(true)}
        />
      </AspectRatio>

      <Box
        position={'absolute'}
        top={{ base: '10px', md: '12px' }}
        left={{ base: '10px', md: '12px' }}
        px={{ base: '9px', md: '10px' }}
        py={{ base: '4px', md: '5px' }}
        borderRadius={'999px'}
        bg={EMISSION_LOADING_COLORS.sponsoredChipBg}
      >
        <Text
          fontFamily={FONT_ONEST}
          fontSize={{ base: '10px', md: '11px' }}
          fontWeight={600}
          lineHeight={'normal'}
          letterSpacing={'0.3px'}
          color={EMISSION_LOADING_COLORS.sponsoredChipText}
        >
          Patrocinado
        </Text>
      </Box>
    </Box>
  );
}
